import { useEffect, useState, useCallback } from 'react'
import { getLocalApiUrl, authFetch } from '../config/api'

/**
 * Resolve the active attestation wording for a given signer role.
 *
 * Consumers: any capture / signing UI (web PayoutCapture, future mobile
 * bridge, ECDD attestation, etc.) that displays attestation text to a
 * human and needs to stamp which version was shown on the resulting
 * record. See backend/src/routes/attestation-versions.js for the
 * resolver contract (tenant → platform × code × locale, effective at
 * asOf).
 *
 * @param {string} code  Attestation role — e.g. 'patron_payout_receipt',
 *                       'cashier_payout_confirm'. Must match a seeded
 *                       code or a tenant-created version.
 * @param {object} [opts]
 * @param {string} [opts.locale]  Defaults to 'en-AU'.
 * @param {Date|string} [opts.asOf]  When omitted the resolver uses the
 *                       server clock at request time. Pass a specific
 *                       date only when rendering a historical record.
 *
 * @returns {{ data: object|null, loading: boolean, error: string|null, reload: () => void }}
 *   - data.id           the version id to stamp on the record
 *   - data.body_text    the text to display
 *   - data.version_label / effective_from / regime_version / metadata
 *     also available for richer UIs.
 */
export function useAttestationVersion(code, { locale, asOf } = {}) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    if (!code) { setData(null); setLoading(false); return }
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ code })
      if (locale) params.set('locale', locale)
      if (asOf) params.set('asOf', asOf instanceof Date ? asOf.toISOString() : String(asOf))
      const res = await authFetch(getLocalApiUrl(`/api/attestation-versions/active?${params.toString()}`))
      const json = await res.json().catch(() => ({}))
      if (res.status === 404) {
        // No active version — caller surfaces placeholder text. Not an error.
        setData(null)
        return
      }
      if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`)
      setData(json.data)
    } catch (e) {
      setError(e.message || 'Failed to load attestation wording')
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [code, locale, asOf])

  useEffect(() => { load() }, [load])

  return { data, loading, error, reload: load }
}
